import { CareNet, SystemName } from 'constants/captions'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { setDocumentTitle } from 'shared/util'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography
} from '@mui/material'

import { OkIcon } from 'shared/icons'

import { CheckCircle as FinishedIcon } from '@mui/icons-material'

import { Loading } from 'components/controls/loading'

import { TenantResult, GetTenant, GetTenantBackground, GetTenantBanner } from 'interface/tenant'

import { useInviteRequestForm, InviteRequestForm } from './form'

import { useParams } from 'react-router-dom'
import { InviteRequestFormData, PostInviteRequest } from 'interface/user/invite'

export const InviteRequestPage = () => {
  const { domain } = useParams()
  const params = useMemo(
    () => ({
      domain: String(domain)
    }),
    [domain]
  )
  const [isPosting, setIsPosting] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<{ header: string, body: string | null } | null>(null)
  const [step, setStep] = useState<'input' | 'finished'>('input')

  const [tenant, setTenant] = useState<TenantResult | undefined>()
  const [tenantBackground, setTenantBackground] = useState<string | undefined>()
  const [tenantBanner, setTenantBanner] = useState<string | undefined>()

  const requestForm = useInviteRequestForm(params.domain)

  useEffect(
    () => {
      if (!domain) return

      GetTenant(domain)
        .catch((reason) => console.error('failed to get tenant', reason))
        .then((result) => setTenant(result ?? undefined))

      GetTenantBackground(domain)
        .catch((reason) => console.error('failed to get tenant background', reason))
        .then((response) =>
          setTenantBackground(
            response ? URL.createObjectURL(response.blob) : undefined
          )
        )
      GetTenantBanner(domain)
        .catch((reason) => console.error('failed to get tenant bunner', reason))
        .then((response) =>
          setTenantBanner(
            response ? URL.createObjectURL(response.blob) : undefined
          )
        )
    },
    [domain]
  )

  const onRequest = useCallback(
    async (form: InviteRequestFormData) => {
      setIsPosting(true)
      setErrorMessage(null)
      try {
        await PostInviteRequest(params.domain, form)
        setStep('finished')
      } catch (e) {
        console.error('failed to send request', e)
        let errorBody = null
        if (typeof e === 'object') {
          const err = e as { errorCode?: number, message?: string }
          if (err.errorCode === 107) {
            errorBody = '登録済のメールアドレスです'
          } else if (err.message) {
            errorBody = err.message
          } else {
            errorBody = JSON.stringify(err.message, null, 2)
          }
        }
        setErrorMessage({ header: '登録申請に失敗しました', body: errorBody })
      } finally {
        setIsPosting(false)
      }
    },
    [params, setErrorMessage, setStep]
  )

  useEffect(() => setDocumentTitle('ユーザー登録申請', tenant?.tenantName), [tenant])

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{ backgroundColor: '#f0f0f0', minHeight: '100vh' }}
    >
      {isPosting && <Loading/>}

      <Container maxWidth="md">
        <Paper style={{ overflow: 'hidden' }}>
          <Box position="relative">
            {tenantBackground && (
              <img
                src={tenantBackground}
                alt={domain}
                style={{
                  objectFit: "cover",
                  width: "100%",
                  height: "25vh"
                }}
              />
            )}
            {tenantBanner && (
              <Box position="absolute" top="0.5em" left="0.5em">
                <img
                  src={tenantBanner}
                  alt={domain}
                  style={{
                    borderRadius: "0.2em"
                  }}
                />
              </Box>
            )}
          </Box>
          <Stack direction="column" spacing={2} padding={5}>
            {step === 'input' ? (
              <>
                <InviteRequestForm {...requestForm}/>

                {errorMessage && (
                  <Alert severity="error">
                    <AlertTitle>{errorMessage.header}</AlertTitle>
                    {errorMessage.body && (
                      <Typography component="pre" variant='body2'>
                        {errorMessage.body}
                      </Typography>
                    )}
                  </Alert>
                )}
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    disabled={!requestForm.form.formState.isValid}
                    variant="contained"
                    onClick={requestForm.form.handleSubmit(
                      input => onRequest(requestForm.submit(input))
                    )}
                    size="large"
                    startIcon={<OkIcon/>}
                  >
                    申 請
                  </Button>
                </Stack>
              </>
            ) : step === 'finished' ? (
              <>
                <Stack direction="column" alignItems="center" textAlign="center">
                  <FinishedIcon color="success" fontSize="large"/>
                  <Typography variant="h5" marginTop={1}>
                    登録申請を送信しました
                  </Typography>
                </Stack>

                <Typography component="pre" variant='body1' textAlign="center">
                  管理者から承認された後、メールが届きます<br/>
                  メール内に記載されているURLからログインしてください<br/>
                  メールが届かない場合はお手数ですが<br/>
                  管理者までお問い合わせください
                </Typography>
              </>
            ) : <></> }
          </Stack>
        </Paper>

        <Typography component="pre" variant='caption' textAlign="center" marginTop={2} color="GrayText">
          {CareNet} {SystemName} @ 2025
        </Typography>
      </Container>
    </Stack>
  )
}
